import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getCategories } from '@services/product.service'
import { getProductById, createProduct, updateProduct, uploadImage } from '@services/admin.service'
import './ProductForm.scss'

function slugify(text) {
  if (!text || typeof text !== 'string') return ''
  return text
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

const emptyVariant = () => ({ name: '', stock: 0 })

export default function AdminProductForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = Boolean(id)

  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [description, setDescription] = useState('')
  const [shortDescription, setShortDescription] = useState('')
  const [price, setPrice] = useState('')
  const [comparePrice, setComparePrice] = useState('')
  const [stockQuantity, setStockQuantity] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [isFeatured, setIsFeatured] = useState(false)
  const [variants, setVariants] = useState([emptyVariant()])
  const [tagsStr, setTagsStr] = useState('')
  const [mainImage, setMainImage] = useState('')

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const catRes = await getCategories()
        const list = catRes?.data ?? catRes ?? []
        if (!cancelled) setCategories(Array.isArray(list) ? list : [])
      } catch {
        /* ignore */
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!isEdit) return
    let cancelled = false
    setLoading(true)
    setError(null)
    getProductById(id)
      .then((res) => {
        const p = res?.data ?? res
        if (cancelled || !p) return
        setName(p.name ?? '')
        setSlug(p.slug ?? '')
        setDescription(p.description ?? '')
        setShortDescription(p.short_description ?? '')
        setPrice(String(p.price ?? ''))
        setComparePrice(p.compare_price != null ? String(p.compare_price) : '')
        setStockQuantity(String(p.stock_quantity ?? ''))
        setCategoryId(p.category_id ?? '')
        setIsFeatured(Boolean(p.is_featured))
        const v = p.variants
        if (Array.isArray(v) && v.length > 0) {
          setVariants(v.map((x) => ({ name: x.name ?? '', stock: Number(x.stock) || 0 })))
        } else {
          setVariants([emptyVariant()])
        }
        setTagsStr(Array.isArray(p.tags) ? p.tags.join(', ') : '')
        setMainImage(p.main_image ?? '')
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.message || e.message || 'Eroare la încărcare')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [id, isEdit])

  const handleNameChange = (v) => {
    setName(v)
    if (!isEdit) setSlug(slugify(v))
  }

  const addVariant = () => setVariants((prev) => [...prev, emptyVariant()])
  const removeVariant = (index) => {
    setVariants((prev) => prev.filter((_, i) => i !== index))
  }
  const updateVariant = (index, field, value) => {
    setVariants((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: field === 'stock' ? (Number(value) || 0) : value }
      return next
    })
  }

  const handleImageUpload = async (e) => {
    const file = e.target?.files?.[0]
    if (!file) return
    try {
      const res = await uploadImage(file)
      if (res?.url) setMainImage(res.url)
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Eroare la încărcare imagine')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    const numPrice = parseFloat(price)
    if (!name.trim()) {
      setError('Numele este obligatoriu.')
      return
    }
    if (price === '' || isNaN(numPrice) || numPrice < 0) {
      setError('Prețul trebuie să fie un număr >= 0.')
      return
    }
    setSaving(true)
    try {
      const payload = {
        name: name.trim(),
        slug: (slug || slugify(name)).trim() || slugify(name),
        description: description.trim(),
        short_description: shortDescription.trim() || null,
        price: numPrice,
        compare_price: comparePrice === '' ? null : parseFloat(comparePrice),
        stock_quantity: parseInt(stockQuantity, 10) || 0,
        category_id: categoryId || null,
        is_featured: isFeatured,
        variants: variants.filter((v) => v.name.trim()).map((v) => ({ name: v.name.trim(), stock: Number(v.stock) || 0 })),
        tags: tagsStr.trim() ? tagsStr.split(',').map((s) => s.trim()).filter(Boolean) : [],
        main_image: mainImage || null,
      }
      if (isEdit) {
        await updateProduct(id, payload)
        navigate('/admin/products', { replace: true })
      } else {
        await createProduct(payload)
        navigate('/admin/products', { replace: true })
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Eroare la salvare')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="admin-page">Se încarcă...</div>

  return (
    <div className="admin-page">
      <form className="admin-card product-form" onSubmit={handleSubmit}>
        {error && <p className="product-form__error">{error}</p>}

        <div className="admin-form-group">
          <label>Nume *</label>
          <input type="text" value={name} onChange={(e) => handleNameChange(e.target.value)} required />
        </div>
        <div className="admin-form-group">
          <label>Slug</label>
          <input type="text" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="Generat din nume" />
        </div>
        <div className="admin-form-group">
          <label>Descriere</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="admin-form-group">
          <label>Descriere scurtă</label>
          <input type="text" value={shortDescription} onChange={(e) => setShortDescription(e.target.value)} />
        </div>
        <div className="admin-form-group">
          <label>Preț (RON) *</label>
          <input type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} required />
        </div>
        <div className="admin-form-group">
          <label>Preț comparare (RON)</label>
          <input type="number" step="0.01" min="0" value={comparePrice} onChange={(e) => setComparePrice(e.target.value)} />
        </div>
        <div className="admin-form-group">
          <label>Stoc</label>
          <input type="number" min="0" value={stockQuantity} onChange={(e) => setStockQuantity(e.target.value)} />
        </div>
        <div className="admin-form-group">
          <label>Categorie</label>
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">—</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="admin-form-group product-form__checkbox">
          <label>
            <input type="checkbox" checked={isFeatured} onChange={(e) => setIsFeatured(e.target.checked)} />
            Produs recomandat (featured)
          </label>
        </div>

        <div className="admin-form-group">
          <label>Variante (nume + stoc)</label>
          <div className="product-form__variants">
            {variants.map((v, i) => (
              <div key={i} className="product-form__variant-row">
                <input
                  type="text"
                  placeholder="Nume variantă"
                  value={v.name}
                  onChange={(e) => updateVariant(i, 'name', e.target.value)}
                />
                <input
                  type="number"
                  min="0"
                  placeholder="Stoc"
                  value={v.stock}
                  onChange={(e) => updateVariant(i, 'stock', e.target.value)}
                />
                <button type="button" className="product-form__btn-remove" onClick={() => removeVariant(i)}>Șterge</button>
              </div>
            ))}
            <button type="button" className="btn-ghost product-form__btn-add" onClick={addVariant}>+ Adaugă variantă</button>
          </div>
        </div>

        <div className="admin-form-group">
          <label>Tag-uri (separate prin virgulă)</label>
          <input type="text" value={tagsStr} onChange={(e) => setTagsStr(e.target.value)} placeholder="ex: lavandă, relaxare" />
        </div>

        <div className="admin-form-group">
          <label>Imagine principală</label>
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageUpload} />
          {mainImage && (
            <p className="product-form__image-preview">
              <img src={mainImage} alt="Preview" />
            </p>
          )}
        </div>

        <div className="product-form__actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Se salvează...' : isEdit ? 'Actualizează' : 'Creează produs'}
          </button>
          <button type="button" className="btn-ghost" onClick={() => navigate('/admin/products')}>
            Anulare
          </button>
        </div>
      </form>
    </div>
  )
}
